/**
 * Unified Launch Service
 * Single source of truth for all launch operations with feature flag support
 */

import { httpsCallable } from 'firebase/functions';
import { functions } from '../utils/firebase';

// Feature flag for unified sessions
export const useUnifiedSessions = false; // Disabled until deployment issues are resolved

// Unified callable functions
const startLaunchSessionFn = httpsCallable(functions, 'startLaunchSession');
const getLaunchStatusFn = httpsCallable(functions, 'getLaunchStatus');
const createOverageCheckoutFn = httpsCallable(functions, 'createOverageCheckout');

/**
 * Start a launch session (unified system)
 * @param {string} gameId - Game ID to launch
 * @returns {Promise<Object>} Launch session data
 */
export async function startLaunchSession(gameId) {
  console.info('launchService.startLaunchSession', {
    op: 'startLaunchSession',
    gameId,
    functionName: 'startLaunchSessionFn'
  });

  try {
    const result = await startLaunchSessionFn({ gameId });
    return result.data;
  } catch (error) {
    console.error('Error starting launch session:', error);
    
    // Fallback: Create session directly in Firestore
    console.warn('Using fallback session creation - unified system not available');
    const { doc, setDoc, serverTimestamp } = await import('firebase/firestore');
    const { db } = await import('../utils/firebase');
    const { getAuth } = await import('firebase/auth');
    
    const auth = getAuth();
    const user = auth.currentUser;
    if (!user) throw new Error('User not authenticated');
    
    const sessionId = `${user.uid}_${gameId}`;
    const sessionRef = doc(db, 'sessions', sessionId);
    const now = Date.now();
    const expiresAt = now + (12 * 60 * 60 * 1000); // 12 hours
    
    await setDoc(sessionRef, {
      ownerUid: user.uid,
      gameId: gameId,
      status: 'active',
      startedAt: now,
      expiresAt: expiresAt, // Keep as number for now
      source: 'fallback_creation',
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp()
    });
    
    return {
      ok: true,
      sessionId: sessionId,
      startedAt: now,
      expiresAt: expiresAt,
      isNewSession: true,
      source: 'fallback'
    };
  }
}

/**
 * Get launch status (unified system)
 * @param {string} gameId - Game ID to check
 * @returns {Promise<Object>} Launch status data
 */
export async function getLaunchStatus(gameId) {
  console.info('launchService.getLaunchStatus', {
    op: 'getLaunchStatus',
    gameId,
    functionName: 'getLaunchStatusFn'
  });

  try {
    const result = await getLaunchStatusFn({ gameId });
    return result.data;
  } catch (error) {
    console.error('Error getting launch status:', error);
    
    // Fallback: Check session directly in Firestore
    console.warn('Using fallback session check - unified system not available');
    const { doc, getDoc } = await import('firebase/firestore');
    const { db } = await import('../utils/firebase');
    const { getAuth } = await import('firebase/auth');
    
    const auth = getAuth();
    const user = auth.currentUser;
    if (!user) throw new Error('User not authenticated');
    
    const sessionId = `${user.uid}_${gameId}`;
    const sessionRef = doc(db, 'sessions', sessionId);
    const sessionSnap = await getDoc(sessionRef);
    
    if (sessionSnap.exists()) {
      const sessionData = sessionSnap.data();
      const now = Date.now();
      const isActive = sessionData.status === 'active' && sessionData.expiresAt > now;
      
      return {
        ok: true,
        status: isActive ? 'active' : 'expired',
        session: isActive ? {
          sessionId: sessionSnap.id,
          gameId: sessionData.gameId,
          startedAt: sessionData.startedAt,
          expiresAt: sessionData.expiresAt,
          status: sessionData.status,
          source: sessionData.source
        } : null
      };
    } else {
      return {
        ok: true,
        status: 'none',
        session: null
      };
    }
  }
}

/**
 * Create overage checkout (unified system)
 * @param {string} gameId - Game ID for overage checkout
 * @returns {Promise<Object>} Checkout session data
 */
export async function createOverageCheckout(gameId) {
  console.info('launchService.createOverageCheckout', {
    op: 'createOverageCheckout',
    gameId,
    functionName: 'createOverageCheckoutFn'
  });

  try {
    const result = await createOverageCheckoutFn({ gameId });
    return result.data;
  } catch (error) {
    console.error('Error creating overage checkout:', error);
    throw error;
  }
}

/**
 * Legacy fallback functions (for safety during transition)
 * These will be removed once unified system is verified in production
 */

// Legacy HTTP-based startGameLaunch (fallback only)
export async function startGameLaunchLegacy(gameId) {
  if (!useUnifiedSessions) {
    console.warn('Using legacy startGameLaunch - should migrate to unified system');
    // Implementation would go here if needed for fallback
    throw new Error('Legacy system disabled - use unified sessions');
  }
  throw new Error('Legacy system not available');
}

// Legacy callable-based launchPurchasedGame (fallback only)
export async function launchPurchasedGameLegacy(gameId) {
  if (!useUnifiedSessions) {
    console.warn('Using legacy launchPurchasedGame - should migrate to unified system');
    // Implementation would go here if needed for fallback
    throw new Error('Legacy system disabled - use unified sessions');
  }
  throw new Error('Legacy system not available');
}
